import { DateTime as _DateTime, Duration as _Duration, Interval as _Interval } from 'luxon';

export type DateTimeType = _DateTime;
export const DateTime = _DateTime;
export const Duration = _Duration;
export const Interval = _Interval;

export const DATE_TIME_FORMAT_24 = "dd/MM/yyyy 'at' HH:mm";
export const DATE_TIME_FORMAT = "dd/MM/yyyy 'at' h:mm a";

// Zoom-specific date utility that handles array formats from backend
export const getZoomDateTime = (date: string | Date | number[]) => {
  if (typeof date === 'string') {
    return DateTime.fromISO(date, { zone: 'utc' }).toLocal();
  } else if (date instanceof Date) {
    return DateTime.fromJSDate(date, { zone: 'utc' }).toLocal();
  } else if (Array.isArray(date) && date.length >= 5) {
    // Handle array format [year, month, day, hour, minute] from Zoom backend
    const [year, month, day, hour, minute] = date;
    return DateTime.fromObject(
      {
        year,
        month,
        day,
        hour,
        minute,
        second: 0,
        millisecond: 0
      },
      { zone: 'utc' }
    ).toLocal();
  } else if (Array.isArray(date) && date.length >= 3) {
    // Handle array format with just [year, month, day]
    const [year, month, day] = date;
    return DateTime.fromObject(
      {
        year,
        month,
        day,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
      },
      { zone: 'utc' }
    ).toLocal();
  } else {
    // Return invalid DateTime for unsupported formats
    return DateTime.invalid('Unsupported date format');
  }
};

export const getZoomDateISOForServer = (date: string | Date | number[]) => {
  const datetime = getZoomDateTime(date);
  return datetime.isValid ? datetime.toUTC().toISO({ includeOffset: false, includePrefix: false }) : null;
};

export function formatZoomDateAndTime(date: string | Date | number[], use24HourFormat = true) {
  const datetime = getZoomDateTime(date);
  if (!datetime.isValid) {
    return '--/--/---- at --:--';
  }
  return datetime.toFormat(use24HourFormat ? DATE_TIME_FORMAT_24 : DATE_TIME_FORMAT);
}

export function formatZoomDate(date: string | Date | number[]) {
  const datetime = getZoomDateTime(date);
  if (!datetime.isValid) {
    return '--/--/----';
  }
  return datetime.toLocaleString(DateTime.DATE_SHORT);
}

export function formatZoomTime(time: string | Date | number[]) {
  const datetime = getZoomDateTime(time);
  if (!datetime.isValid) {
    return '--:--';
  }
  return datetime.toLocaleString(DateTime.TIME_SIMPLE);
}

export function isZoomDateTime(date: string) {
  const datetime = DateTime.fromISO(date);
  return datetime.hour !== 0 || datetime.minute !== 0 || datetime.second !== 0;
}

export function subtractZoomDates(minuend: string | Date | number[], subtrahend: string | Date | number[]) {
  return getZoomDateTime(minuend).diff(getZoomDateTime(subtrahend));
}

export const getZoomRelativeFormat = (date: string | Date | number[]) => {
  const datetime = getZoomDateTime(date);
  if (!datetime.isValid) {
    return '';
  }

  const units: Intl.RelativeTimeFormatUnit[] = ['year', 'month', 'week', 'day', 'hour', 'minute', 'second'];
  const diff = datetime.diffNow().shiftTo(...units);
  const unit = units.find((unit) => diff.get(unit) !== 0) || 'second';

  const relativeFormatter = new Intl.RelativeTimeFormat('en', {
    numeric: 'auto'
  });
  return relativeFormatter.format(Math.trunc(diff.as(unit)), unit);
};
